import { resolveLocale } from '@ccex/shared';
import { createPinia } from 'pinia';
import { createApp } from 'vue';
import App from './App.vue';
import { loadRuntimeConfig } from './config/runtime';
import { i18n, setLocale } from './i18n';
import { initMonitoring } from './monitoring/sentry';
import { router } from './router';
import { useSessionStore } from './stores/session';
import { mockEnvIfNeeded } from './telegram/env';
import { readInviteToken } from './telegram/invite-token';
import { setupTelegram } from './telegram/sdk';
import './styles/main.css';

// A throw here (e.g. an unusual host, or an unmocked non-Telegram environment) must not leave a
// permanently blank screen: fall back to an empty context so the app still mounts and the
// Loading screen's error state / Retry button takes over. Never log initData contents (PII).
let telegram: { rawInitData: string; languageCode: string | undefined };
try {
  mockEnvIfNeeded();
  telegram = setupTelegram();
} catch (error) {
  console.error(
    'Failed to initialise the Telegram environment; falling back to empty context.',
    error,
  );
  telegram = { rawInitData: '', languageCode: undefined };
}

const pinia = createPinia();
const app = createApp(App).use(pinia).use(i18n).use(router);

useSessionStore(pinia).setRawInitData(telegram.rawInitData);
useSessionStore(pinia).setInviteToken(readInviteToken(window.location.search));
const locale = resolveLocale(telegram.languageCode);
setLocale(locale);

async function bootstrap(): Promise<void> {
  // Monitoring is optional: no config (dev, tests, a broken ConfigMap) just means it stays off.
  const config = await loadRuntimeConfig();
  if (config) {
    try {
      initMonitoring(app, router, config, locale);
    } catch (error) {
      // Best-effort: a failed monitoring init must never keep the app from mounting.
      console.error('Monitoring init failed; continuing without it.', error);
    }
  }
  app.mount('#app');
  // Tells the inline boot script in index.html that Vue is up, so its panel stays hidden.
  window.__ccexBoot?.markMounted();
}

void bootstrap();
